import React from 'react';
import {
  Paper, Stack, Title, TextInput, PasswordInput, Button, Box, Image, Overlay, Center,
} from '@mantine/core';
import { MediaStack, isMediaPresent } from '../../../media';
import { buttonAppearance } from '../shared/buttonAppearance';
import VariableImage from '../../../media/VariableImage';
import { withVariablePlaceholder } from '../../../media/variableMedia';
import PuckInlineRichText from '../../proposal/ProposalRichText/PuckInlineRichText';
import classes from './LoginPage.module.css';

/**
 * Formulaire de login par défaut — utilisé en mode éditeur (aperçu non fonctionnel).
 * En viewer, le formulaire fonctionnel est injecté via la prop `formSlot`.
 */
const LoginForm = ({ isEditing, submitAppearance }) => (
  <Stack gap="md" w="100%" maw={360}>
    <TextInput label="Email" placeholder="votre@email.com" type="email" disabled={isEditing} />
    <PasswordInput label="Mot de passe" placeholder="••••••••" disabled={isEditing} />
    {/* Aperçu d'éditeur : le formulaire fonctionnel est injecté par `formSlot`.
        Les deux boutons partagent la même apparence, sinon l'aperçu mentirait —
        d'où un bouton inerte plutôt que `disabled`, dont le gris masquait la
        couleur choisie. */}
    <Button
      type={isEditing ? 'button' : 'submit'}
      {...submitAppearance.buttonProps}
      fullWidth
      className={submitAppearance.className}
    >
      Se connecter
    </Button>
  </Stack>
);

/**
 * Titre d'accueil + sous-titre édités inline sur le canvas (texte riche, toolbar
 * au survol) — plus des champs sidebar. Rien n'est rendu sur le site quand un
 * texte est vide ; en édition une puce réservée reste cliquable (même garde-fou
 * que LoadingPage). Couleurs/tailles passées par variante ; l'utilisateur peut
 * ensuite recolorer via la toolbar.
 */
const WelcomeText = ({
  id,
  puck,
  welcomeTitle,
  welcomeSubtitle,
  align = 'center',
  titleColor,
  subtitleColor,
  titleFontSize = '1.5rem',
}) => {
  const isEditing = !!puck?.isEditing;
  const showTitle = isEditing || welcomeTitle;
  const showSubtitle = isEditing || welcomeSubtitle;
  if (!showTitle && !showSubtitle) return null;
  const textAlign = align === 'center' ? 'center' : 'left';
  return (
    <Stack gap={4} w="100%" align={align === 'center' ? 'center' : 'stretch'}>
      {showTitle && (
        <PuckInlineRichText
          id={id}
          puck={puck}
          html={welcomeTitle}
          contentProp="welcomeTitle"
          placeholder="Titre d'accueil"
          reducedToolbar
          alignHorizontal={align}
          minHeight={isEditing ? 30 : undefined}
          style={{
            fontSize: titleFontSize,
            fontWeight: 700,
            lineHeight: 1.2,
            textAlign,
            width: '100%',
            ...(titleColor ? { color: titleColor } : {}),
          }}
        />
      )}
      {showSubtitle && (
        <PuckInlineRichText
          id={id}
          puck={puck}
          html={welcomeSubtitle}
          contentProp="welcomeSubtitle"
          placeholder="Sous-titre"
          reducedToolbar
          alignHorizontal={align}
          minHeight={isEditing ? 24 : undefined}
          style={{
            fontSize: '0.875rem',
            textAlign,
            width: '100%',
            color: subtitleColor || 'var(--mantine-color-dimmed)',
          }}
        />
      )}
    </Stack>
  );
};

const LoginMinimal = ({ id, puck, renderLogo, welcomeTitle, welcomeSubtitle, backgroundColor, isEditing, formSlot }) => (
  <Box className={classes.minimal} style={{ backgroundColor }}>
    <Paper className={classes.minimalCard} shadow="md" p="xl" radius="lg" withBorder>
      <Stack align="center" gap="lg">
        {renderLogo(48)}
        <WelcomeText id={id} puck={puck} welcomeTitle={welcomeTitle} welcomeSubtitle={welcomeSubtitle} align="center" />
        {formSlot}
      </Stack>
    </Paper>
  </Box>
);

const LoginSplit = ({
  id,
  puck,
  renderLogo,
  welcomeTitle,
  welcomeSubtitle,
  backgroundColor,
  backgroundImage,
  mediaFit,
  mediaAspectRatio,
  isEditing,
  formSlot,
}) => (
  <Box className={classes.split}>
    <Box className={classes.splitLeft} style={{ backgroundColor: backgroundImage ? undefined : backgroundColor }}>
      {isMediaPresent(backgroundImage) ? (
        <MediaStack
          imageUrl={backgroundImage}
          mediaFit={mediaFit}
          mediaAspectRatio={mediaAspectRatio}
          isEditing={isEditing}
          asBackground
          fillContainer
          overlay={<Overlay color="#000" opacity={0.4} zIndex={0} />}
          className={classes.splitMedia}
          contentClassName={classes.splitLeftContentWrap}
        >
          <Stack className={classes.splitLeftContent} gap="md" align="flex-start">
            {renderLogo(40)}
            <WelcomeText
              id={id}
              puck={puck}
              welcomeTitle={welcomeTitle}
              welcomeSubtitle={welcomeSubtitle}
              align="left"
              titleFontSize="1.75rem"
              titleColor="#fff"
              subtitleColor="rgba(255,255,255,0.85)"
            />
          </Stack>
        </MediaStack>
      ) : (
        <Stack className={classes.splitLeftContent} gap="md" align="flex-start" p="xl">
          {renderLogo(40)}
          <WelcomeText
            id={id}
            puck={puck}
            welcomeTitle={welcomeTitle}
            welcomeSubtitle={welcomeSubtitle}
            align="left"
            titleFontSize="1.75rem"
          />
        </Stack>
      )}
    </Box>
    <Center className={classes.splitRight} style={{ backgroundColor }}>
      <Stack gap="lg" align="center" w="100%" maw={380} px="xl">
        <Title order={3}>Connexion</Title>
        {formSlot}
      </Stack>
    </Center>
  </Box>
);

const LoginFull = ({
  id,
  puck,
  renderLogo,
  welcomeTitle,
  welcomeSubtitle,
  backgroundColor,
  backgroundImage,
  mediaFit,
  mediaAspectRatio,
  isEditing,
  formSlot,
}) => (
  <Box className={`${classes.full} ${isEditing ? classes.fullEditing : ''}`.trim()} style={{ backgroundColor: backgroundImage ? undefined : backgroundColor }}>
    {isMediaPresent(backgroundImage) ? (
      <MediaStack
        imageUrl={backgroundImage}
        mediaFit={mediaFit}
        mediaAspectRatio={mediaAspectRatio}
        isEditing={isEditing}
        asBackground
        fillContainer
        overlay={<Overlay color="#000" opacity={0.5} zIndex={0} />}
        className={classes.fullMedia}
        contentClassName={classes.fullContentWrap}
      >
        <Center className={classes.fullContent}>
          <Paper shadow="xl" p="xl" radius="lg" withBorder className={classes.fullCard}>
            <Stack align="center" gap="lg">
              {renderLogo(48)}
              <WelcomeText id={id} puck={puck} welcomeTitle={welcomeTitle} welcomeSubtitle={welcomeSubtitle} align="center" />
              {formSlot}
            </Stack>
          </Paper>
        </Center>
      </MediaStack>
    ) : (
      <Center className={classes.fullContent}>
        <Paper shadow="xl" p="xl" radius="lg" withBorder className={classes.fullCard}>
          <Stack align="center" gap="lg">
            {renderLogo(48)}
            <WelcomeText id={id} puck={puck} welcomeTitle={welcomeTitle} welcomeSubtitle={welcomeSubtitle} align="center" />
            {formSlot}
          </Stack>
        </Paper>
      </Center>
    )}
  </Box>
);

const LoginPage = ({
  id,
  variant = 'minimal',
  backgroundImage = '',
  backgroundColor = '#f5f7fa',
  logoUrl = '',
  logoUrlVariable = null,
  welcomeTitle = 'Bienvenue',
  welcomeSubtitle = 'Connectez-vous pour accéder au document.',
  mediaFit = 'cover',
  mediaAspectRatio = 'auto',
  /** Apparence du bouton « Se connecter » — mêmes réglages que la box Bouton. */
  submitColor = '',
  submitBorder = undefined,
  submitButtonStyle = undefined,
  formSlot = null,
  puck,
}) => {
  const isEditing = !!puck?.isEditing;
  const submitAppearance = buttonAppearance({
    color: submitColor || undefined,
    border: submitBorder,
    buttonStyle: submitButtonStyle,
  });
  const renderLogo = (height) => (
    <VariableImage
      blockId={id}
      urlProp="logoUrl"
      src={logoUrl}
      binding={logoUrlVariable}
      isEditing={isEditing}
      renderImage={(src) => src && <Image src={src} h={height} w="auto" fit="contain" alt="Logo" />}
    />
  );
  const commonProps = {
    id,
    puck,
    renderLogo,
    welcomeTitle,
    welcomeSubtitle,
    backgroundColor,
    backgroundImage: withVariablePlaceholder(backgroundImage, { isEditing }),
    mediaFit,
    mediaAspectRatio,
    isEditing,
    formSlot: formSlot || <LoginForm isEditing={isEditing} submitAppearance={submitAppearance} />,
  };

  if (variant === 'split') return <LoginSplit {...commonProps} />;
  if (variant === 'full') return <LoginFull {...commonProps} />;
  return <LoginMinimal {...commonProps} />;
};

export default LoginPage;
